/**
 * The bell's pure helpers (docs/notifications/implementation-guide.md 8.2):
 * where an item opens, how items are ordered and grouped, and how a time
 * reads. No React and no browser globals, so notifications.test.js runs them
 * in node.
 */

/**
 * An item's link screen (notifications/catalogue.js `link`) as the Console's
 * hash route: `#<module>` or `#<module>/<section>` (Console.jsx and each
 * module's section tabs). A screen the Console has no route for opens nothing.
 */
const ROUTES = Object.freeze({
  uploads: 'uploads',
  // Unit Prices now lives on Fund Register.
  unitPrices: 'transactions/registry',
  approval: 'transactions/approval',
  registry: 'transactions/registry',
  reports: 'transactions/reports',
  // Clients live in the Fund Register screen; the business day is the approval screen's chip and drawer.
  clients: 'transactions/registry',
  businessDay: 'transactions/approval',
  analyticsFeeds: 'analyticsFeeds',
  fundConfig: 'settings/fundConfig',
  calendar: 'settings/calendar',
  userManagement: 'userManagement/users',
  profile: 'profile',
});

/** The hash (without '#') an item opens, or null. */
export function hashFor(link) {
  return (link && ROUTES[link.screen]) || null;
}

/**
 * An email's link is `<console>/?open=<screen>&id=<entity>` (templates.js
 * renderNotification). On load the Console turns that into its hash route,
 * once, and drops the query - so a reload does not open it again.
 *
 * @param {string} search  window.location.search
 * @returns {string|null} the hash to open
 */
export function hashFromOpenQuery(search) {
  const params = new URLSearchParams(search || '');
  const screen = params.get('open');
  return screen ? hashFor({ screen }) : null;
}

/** Severities, most urgent first - the drawer's icon colour and the toast rule. */
export const SEVERITY_ORDER = Object.freeze(['CRITICAL', 'WARNING', 'ACTION', 'INFO']);

/** Whether a push of this severity also shows a toast: something went wrong, not routine work. */
export function toastsFor(severity) {
  return severity === 'CRITICAL' || severity === 'WARNING';
}

const COLOMBO_OFFSET_MS = (5 * 60 + 30) * 60_000;
const colomboDay = (ms) => new Date(ms + COLOMBO_OFFSET_MS).toISOString().slice(0, 10);

/**
 * Group items into Today, Yesterday and Earlier, in Colombo time (every
 * Console screen's clock), keeping their order (newest first, as the API
 * answers).
 *
 * @returns {Array<{ label: string, items: object[] }>} only the non-empty groups
 */
export function groupByDay(items, nowMs) {
  const today = colomboDay(nowMs);
  const yesterday = colomboDay(nowMs - 86_400_000);
  const groups = [{ label: 'Today', items: [] }, { label: 'Yesterday', items: [] }, { label: 'Earlier', items: [] }];
  for (const item of items ?? []) {
    const d = colomboDay(Date.parse(item.createdAt));
    groups[d === today ? 0 : d === yesterday ? 1 : 2].items.push(item);
  }
  return groups.filter((g) => g.items.length);
}

/** "just now", "5 min ago", "3 h ago", "2 days ago" - the drawer's time. */
export function relativeTime(iso, nowMs) {
  const seconds = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return Math.floor(seconds / 60) + ' min ago';
  if (seconds < 86_400) return Math.floor(seconds / 3600) + ' h ago';
  const days = Math.floor(seconds / 86_400);
  return days + (days === 1 ? ' day ago' : ' days ago');
}

/** The badge's text: the count, or 99+. */
export function badgeText(unread) {
  const n = Number(unread) || 0;
  return n > 99 ? '99+' : String(n);
}
